/**
 * FocalPoint API Service
 *
 * Service for fetching purchase request data from the FocalPoint
 * (Access FocalPoint \ Access Dimensions) API. Mirrors the FinOps service but targets a
 * separate installation of the same API, reached via the backend /focalpoint route
 * (FE -> BE -> FocalPoint API).
 *
 * Instance ids on this path are FocalPoint/Dimensions scoped: the company-level finance
 * instance is `dimensionsInstanceId` and the matched purchasing instance is
 * `focalpointInstanceId` — never the FinOps/Financials equivalents.
 */

import type { FinOpsPurchaseRequestHeader, FinOpsPurchaseRequestDetail, FinOpsApiConfig, FinOpsTimesheetEntry, FinOpsApprovalHistoryEntry, FinOpsProjectDetails } from '@/types/finops.types';
import type { PurchaseRequest, PurchaseRequestStatus } from '@/types/purchase-request';
import { getIntegrationsFromStorage } from '@/hooks/use-product-integrations';
import { getApiBaseUrl, getDataMode } from './data-service';
import { apiFetch } from './api-error-handler';
import { fetchPurchaseTransactions } from './purchase-transactions-api';
import { toIsoDateStringSafe } from './date-utils';

const FOCALPOINT_PRODUCT_CODE = 'access-focalpoint-purchasing';

/**
 * Get the FocalPoint API configuration if the integration is set up for a specific company
 * Returns null if no FocalPoint integration is configured for the company
 * @param companyId - Optional company ID to filter integrations. If not provided, returns first connected integration.
 */
export function getFocalPointConfig(companyId?: number): FinOpsApiConfig | null {
  const integrations = getIntegrationsFromStorage();
  
  const focalPointIntegration = integrations.find(
    (integration) => 
      integration.productCode === FOCALPOINT_PRODUCT_CODE && 
      integration.status === 'connected' &&
      (companyId ? integration.companyId === String(companyId) : true)
  );
  
  if (!focalPointIntegration) {
    return null;
  }
  
  return {
    baseUrl: getApiBaseUrl(),
    instanceId: focalPointIntegration.instanceId,
  };
}

/**
 * Check if FocalPoint integration is available for a specific company
 * @param companyId - Optional company ID to check. If not provided, checks for any connected integration.
 */
export function isFocalPointIntegrationAvailable(companyId?: number): boolean {
  return getFocalPointConfig(companyId) !== null;
}

/**
 * Map FocalPoint API status to internal PurchaseRequestStatus
 */
function mapFocalPointStatus(status: string): PurchaseRequestStatus {
  switch (status) {
    case 'Approved':
    case 'Ordered':
    case 'Received':
      return 'Approved';
    case 'Posted':
      return 'Posted';
    case 'Rejected':
      return 'Rejected';
    case 'Cancelled':
      return 'Cancelled';
    case 'AwaitingApproval':
    case 'Awaiting Approval':
      return 'Awaiting Approval';
    case 'Draft':
      return 'Draft';
    case 'Pending':
    case 'NotSet':
    case 'Submitted':
      return 'Pending';
    default:
      console.warn(`[mapFocalPointStatus] Unrecognised status: "${status}", defaulting to Pending`);
      return 'Pending';
  }
}

/**
 * PRSH_POSTING_TYPE value for a Financials Purchase Order Credit.
 */
const POSTING_TYPE_PO_CREDIT = 5;

/**
 * Determines whether a FocalPoint PR represents a credit (Purchase Ledger Credit
 * or Purchase Order Credit). Credits are stored as positive values in FocalPoint
 * but must be shown as negative in the Projects context (they reduce
 * committed/logged cost).
 */
function isCreditType(header: FinOpsPurchaseRequestHeader): boolean {
  if (header.postingType === POSTING_TYPE_PO_CREDIT) {
    return true;
  }
  const normalised = (header.type || '').toLowerCase();
  return (
    normalised.includes('pl credit') ||
    normalised.includes('purchase ledger credit') ||
    normalised.includes('po credit') ||
    normalised.includes('purchase order credit')
  );
}

/** PRSH_POSTING_TYPE values that should be hidden from the Purchases tab. */
const EXCLUDED_POSTING_TYPES = new Set([
  4,  // Project Forecast Cost Adjustment
  8,  // Create as Memo Only
]);

const EXCLUDED_POSTING_TYPE_LABELS = new Set([
  'project forecast cost adjustment',
  'memo only',
  'create as memo only',
]);

function isExcludedPostingType(header: FinOpsPurchaseRequestHeader): boolean {
  if (header.postingType != null && EXCLUDED_POSTING_TYPES.has(header.postingType)) {
    return true;
  }
  if (header.type) {
    const normalised = header.type.toLowerCase().trim();
    if (EXCLUDED_POSTING_TYPE_LABELS.has(normalised)) {
      return true;
    }
  }
  return false;
}

/**
 * Map FocalPoint API response to internal PurchaseRequest format
 */
function mapFocalPointToPurchaseRequest(
  header: FinOpsPurchaseRequestHeader,
  projectId: number
): PurchaseRequest {
  const rawAmount = header.total || 0;
  // Credits (PL Credit / PO Credit) come through as positive from FocalPoint but represent negative cost
  const sign = isCreditType(header) ? -1 : 1;
  const amount = sign * Math.abs(rawAmount);

  return {
    id: `finops-${header.primaryKey}`,
    requestId: header.reference || `PR-${header.primaryKey}`,
    description: header.description || '',
    status: mapFocalPointStatus(header.status),
    amount,
    net: header.net ? sign * Math.abs(header.net) : undefined,
    vat: header.vat ? sign * Math.abs(header.vat) : undefined,
    gross: header.gross ? sign * Math.abs(header.gross) : undefined,
    supplier: header.accountCode || header.accountName || '',
    supplierName: header.accountName || '',
    date: toIsoDateStringSafe(header.orderDate) ?? '',
    projectId: String(projectId),
    requestedBy: header.initiator || 'Unknown',
    approvedBy: header.status === 'Approved' ? header.initiator : undefined,
    approvedDate: header.status === 'Approved' ? toIsoDateStringSafe(header.lastEdited) : undefined,
    category: header.type || 'General',
    notes: header.orderNumber ? `Order Number: ${header.orderNumber}` : undefined,
    poNumber: header.orderNumber ? [header.orderNumber] : undefined,
    currency: 'GBP',
    documents: [],
    lines: undefined,
    costCentreCode: header.costCentre || undefined,
    finOpsPrimaryKey: header.primaryKey,
    splitLine: header.splitLine,
  };
}

/**
 * Fetch purchase requests from the FocalPoint API via the backend
 * Routes through: Frontend -> Backend -> FocalPoint API
 * 
 * @param projectCode - The project code to fetch purchase requests for
 * @param projectId - The internal project ID for mapping
 * @param companyId - Optional company ID. In live mode the rows come from the backend purchase-transactions endpoint,
 *   which resolves the instances from it; otherwise it selects the FocalPoint integration from localStorage.
 * @param focalpointInstanceId - Optional instance ID for the live-mode proxy fallback when companyId is unavailable (the matched FocalPoint purchasing instance id, never the Dimensions one)
 * @returns Array of PurchaseRequest objects or null if integration is not available
 */
export async function fetchFocalPointPurchaseRequests(
  projectCode: string,
  projectId: number,
  companyId?: number,
  focalpointInstanceId?: string
): Promise<PurchaseRequest[] | null> {
  const dataMode = getDataMode();

  // Live mode with a company: the backend resolves the purchasing and finance
  // instances from companyId and returns the unified PR → PO → invoice rows.
  if (dataMode === 'live' && companyId) {
    try {
      return await fetchPurchaseTransactions(projectCode, projectId, companyId);
    } catch (error) {
      console.error('Error fetching FocalPoint purchase requests:', error);
      throw error;
    }
  }
  
  // In live mode, use the provided focalpointInstanceId directly
  // In demo mode, look up the config from localStorage
  let config: FinOpsApiConfig | null;
  if (dataMode === 'live' && focalpointInstanceId) {
    config = {
      baseUrl: getApiBaseUrl(),
      instanceId: focalpointInstanceId,
    };
  } else {
    config = getFocalPointConfig(companyId);
  }
  
  if (!config) {
    return null;
  }
  
  if (dataMode !== 'live') {
    return null;
  }
  
  // Validate that the instance ID is present - required for FocalPoint API
  if (!config.instanceId) {
    throw new Error('FocalPoint integration requires a focalpointInstanceId. Please configure the company with a valid FocalPoint instance.');
  }
  
  try {
    // Call the backend endpoint which proxies to FocalPoint API
    // Note: config.baseUrl already includes /api, so we don't add it again
    const url = `${config.baseUrl}/focalpoint/purchase-requests?projectCode=${encodeURIComponent(projectCode)}`;
    
    // Use X-FocalPoint-Instance-Id header for the company's FocalPoint instance ID
    // This is separate from X-Instance-Id which is used for platform subscription lookup
    const data = await apiFetch<FinOpsPurchaseRequestHeader[] | { items?: FinOpsPurchaseRequestHeader[]; data?: FinOpsPurchaseRequestHeader[]; value?: FinOpsPurchaseRequestHeader[]; $values?: FinOpsPurchaseRequestHeader[] }>(url, {
      method: 'GET',
      headers: {
        'X-FocalPoint-Instance-Id': config.instanceId,
      },
    });
    
    let headers: FinOpsPurchaseRequestHeader[] = [];
    if (Array.isArray(data)) {
      headers = data;
    } else if (data && typeof data === 'object') {
      // Try various common response wrapper formats
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        // FocalPoint API returns data in 'list' property
        headers = dataObj.list as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.items)) {
        headers = dataObj.items as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.data)) {
        headers = dataObj.data as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.value)) {
        headers = dataObj.value as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.$values)) {
        headers = dataObj.$values as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.results)) {
        headers = dataObj.results as FinOpsPurchaseRequestHeader[];
      } else if (Array.isArray(dataObj.records)) {
        headers = dataObj.records as FinOpsPurchaseRequestHeader[];
      }
    }
    
    return headers
      .filter((header) => !isExcludedPostingType(header))
      .map((header) => mapFocalPointToPurchaseRequest(header, projectId));
  } catch (error) {
    console.error('Error fetching FocalPoint purchase requests:', error);
    throw error;
  }
}

/**
 * Fetch purchase request approval history from the FocalPoint API via the backend
 * Routes through: Frontend -> Backend -> FocalPoint API
 * 
 * @param recordLink - The record link ID (header primaryKey for non-split-line, detail primaryKey for split-line)
 * @param focalpointInstanceId - The matched FocalPoint instance id (never the Dimensions one)
 * @returns Array of FinOpsApprovalHistoryEntry objects or null if not available
 */
export async function fetchFocalPointPurchaseRequestApprovalHistory(
  recordLink: number,
  focalpointInstanceId: string
): Promise<FinOpsApprovalHistoryEntry[] | null> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    return null;
  }
  
  if (!focalpointInstanceId || recordLink <= 0) {
    return null;
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/focalpoint/purchase-request-approval-history?recordLink=${recordLink}`;
    
    const data = await apiFetch<FinOpsApprovalHistoryEntry[] | { list?: FinOpsApprovalHistoryEntry[]; items?: FinOpsApprovalHistoryEntry[]; data?: FinOpsApprovalHistoryEntry[] }>(url, {
      method: 'GET',
      headers: {
        'X-FocalPoint-Instance-Id': focalpointInstanceId,
      },
    });
    
    let entries: FinOpsApprovalHistoryEntry[] = [];
    if (Array.isArray(data)) {
      entries = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        entries = dataObj.list as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.items)) {
        entries = dataObj.items as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.data)) {
        entries = dataObj.data as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.value)) {
        entries = dataObj.value as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.$values)) {
        entries = dataObj.$values as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.results)) {
        entries = dataObj.results as FinOpsApprovalHistoryEntry[];
      } else if (Array.isArray(dataObj.records)) {
        entries = dataObj.records as FinOpsApprovalHistoryEntry[];
      }
    }
    
    return entries;
  } catch (error) {
    console.error('Error fetching FocalPoint purchase request approval history:', error);
    throw error;
  }
}

/**
 * Fetch purchase request line details from the FocalPoint API via the backend
 * Routes through: Frontend -> Backend -> FocalPoint API
 * 
 * @param projectCode - The project code to fetch line details for
 * @param focalpointInstanceId - The matched FocalPoint purchasing instance id (never the Dimensions one)
 * @returns Array of FinOpsPurchaseRequestDetail objects or null if not available
 */
export async function fetchFocalPointPurchaseRequestDetails(
  projectCode: string,
  focalpointInstanceId: string
): Promise<FinOpsPurchaseRequestDetail[] | null> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    return null;
  }
  
  if (!focalpointInstanceId) {
    return null;
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/focalpoint/purchase-request-details?projectCode=${encodeURIComponent(projectCode)}`;
    
    const data = await apiFetch<FinOpsPurchaseRequestDetail[] | { list?: FinOpsPurchaseRequestDetail[]; items?: FinOpsPurchaseRequestDetail[]; data?: FinOpsPurchaseRequestDetail[] }>(url, {
      method: 'GET',
      headers: {
        'X-FocalPoint-Instance-Id': focalpointInstanceId,
      },
    });
    
    let details: FinOpsPurchaseRequestDetail[] = [];
    if (Array.isArray(data)) {
      details = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        details = dataObj.list as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.items)) {
        details = dataObj.items as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.data)) {
        details = dataObj.data as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.value)) {
        details = dataObj.value as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.$values)) {
        details = dataObj.$values as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.results)) {
        details = dataObj.results as FinOpsPurchaseRequestDetail[];
      } else if (Array.isArray(dataObj.records)) {
        details = dataObj.records as FinOpsPurchaseRequestDetail[];
      }
    }
    
    return details;
  } catch (error) {
    console.error('Error fetching FocalPoint purchase request details:', error);
    throw error;
  }
}

/**
 * Fetch timesheet entries from the FocalPoint API via the backend
 * Routes through: Frontend -> Backend -> FocalPoint API
 * 
 * @param projectCode - The project code to fetch timesheets for
 * @param focalpointInstanceId - The FocalPoint instance ID (from company.timesheetInstanceId for timesheets)
 * @returns Array of FinOpsTimesheetEntry objects or null if not available
 */
export async function fetchFocalPointTimesheets(
  projectCode: string,
  focalpointInstanceId: string
): Promise<FinOpsTimesheetEntry[] | null> {
  const dataMode = getDataMode();
  
  if (dataMode !== 'live') {
    return null;
  }
  
  if (!focalpointInstanceId) {
    return null;
  }
  
  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/focalpoint/timesheets?projectCode=${encodeURIComponent(projectCode)}`;
    
    const data = await apiFetch<FinOpsTimesheetEntry[] | { list?: FinOpsTimesheetEntry[]; items?: FinOpsTimesheetEntry[]; data?: FinOpsTimesheetEntry[] }>(url, {
      method: 'GET',
      headers: {
        'X-FocalPoint-Instance-Id': focalpointInstanceId,
      },
    });
    
    let entries: FinOpsTimesheetEntry[] = [];
    if (Array.isArray(data)) {
      entries = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        entries = dataObj.list as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.items)) {
        entries = dataObj.items as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.data)) {
        entries = dataObj.data as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.value)) {
        entries = dataObj.value as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.$values)) {
        entries = dataObj.$values as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.results)) {
        entries = dataObj.results as FinOpsTimesheetEntry[];
      } else if (Array.isArray(dataObj.records)) {
        entries = dataObj.records as FinOpsTimesheetEntry[];
      }
    }
    
    return entries;
  } catch (error) {
    console.error('Error fetching FocalPoint timesheets:', error);
    throw error;
  }
}

/**
 * Fetch project-level financial details from the FocalPoint API via the backend.
 * Routes through: Frontend -> Backend -> FocalPoint API (GET /fins/projectdetails).
 *
 * Returns the project details object (actualSales, actualCost, etc.) or null
 * when unavailable (not live mode, no focalpointInstanceId, or empty response).
 *
 * @param projectCode - The project code to fetch details for
 * @param focalpointInstanceId - The matched FocalPoint instance id (never the Dimensions one)
 */
export async function fetchFocalPointProjectDetails(
  projectCode: string,
  focalpointInstanceId: string
): Promise<FinOpsProjectDetails | null> {
  const dataMode = getDataMode();

  if (dataMode !== 'live') {
    return null;
  }

  if (!focalpointInstanceId || !projectCode) {
    return null;
  }

  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/focalpoint/projectdetails?projectCode=${encodeURIComponent(projectCode)}`;

    const data = await apiFetch<FinOpsProjectDetails>(url, {
      method: 'GET',
      headers: {
        'X-FocalPoint-Instance-Id': focalpointInstanceId,
      },
    });

    if (!data || typeof data.actualSales !== 'number' || typeof data.actualCost !== 'number') {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching FocalPoint project details:', error);
    throw error;
  }
}
